import type { JSX } from 'react';
import { Clock, LogOut, Route as RouteIcon, Users } from 'lucide-react';

import { EmptyState } from './EmptyState';
import styles from './MetricsSummary.module.scss';
import type { UsabilityMetric } from '../api/usability.graphql';

function KpiTile({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ size?: number }>;
}): JSX.Element {
  return (
    <div className={styles.kpiTile}>
      <div className={styles.kpiIcon}>
        <Icon size={18} />
      </div>
      <div>
        <div className={styles.kpiValue}>{value}</div>
        <div className={styles.kpiLabel}>{label}</div>
      </div>
    </div>
  );
}

export function MetricsSummary({ metrics }: { metrics: UsabilityMetric[] }): JSX.Element {
  if (metrics.length === 0) {
    return <EmptyState message="Sin métricas de usabilidad para el rango seleccionado" />;
  }

  const totalSessions = metrics.reduce((sum, m) => sum + m.sessionsCount, 0);
  const avgScreenTime = Math.round(
    metrics.reduce((sum, m) => sum + m.avgScreenTimeMs, 0) / metrics.length,
  );
  const avgExitRate = metrics.reduce((sum, m) => sum + m.exitRate, 0) / metrics.length;

  return (
    <div className={styles.wrapper}>
      <div className={styles.kpiGrid}>
        <KpiTile label="Sesiones totales" value={totalSessions} icon={Users} />
        <KpiTile label="Tiempo promedio en pantalla" value={`${avgScreenTime} ms`} icon={Clock} />
        <KpiTile label="Tasa de abandono promedio" value={`${Math.round(avgExitRate * 100)}%`} icon={LogOut} />
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Página</th>
              <th>Módulo</th>
              <th>Sesiones</th>
              <th>Tiempo prom.</th>
              <th>Abandono</th>
              <th>Ruta más frecuente</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m) => (
              <tr key={`${m.page}-${m.date}`}>
                <td>{m.page}</td>
                <td>{m.module ?? '—'}</td>
                <td>{m.sessionsCount}</td>
                <td>{m.avgScreenTimeMs} ms</td>
                <td>{Math.round(m.exitRate * 100)}%</td>
                <td>
                  {m.topNextPage ? (
                    <span className={styles.routeCell}>
                      <RouteIcon size={12} /> {m.topNextPage}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
